import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import CloseIcon from "@mui/icons-material/Close";
import VideocamIcon from "@mui/icons-material/Videocam";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import toast from "react-hot-toast";
import {
  deletePatrolSectionCamera,
  fetchPatrolSectionCameras,
} from "../../../api/patrol.ts";
import PatrolDrawingMediaSurface from "./PatrolDrawingMediaSurface.jsx";
import {
  DEFAULT_LOCATION_DURATION,
  countPlacementLocations,
  enrichPlacementsWithCameraLabels,
  getDrawingCameraPlacements,
  markersForDisplay,
  normalizePlacements,
  placementDisplayLabel,
} from "./patrolSectionCameraLocation.js";

/**
 * @param {{
 *   open: boolean,
 *   drawing: object | null,
 *   cameras: Array<{ id: string, name: string, site: string, type: string }>,
 *   camerasLoading?: boolean,
 *   onClose: () => void,
 *   onSave: (drawingId: string, cameraPlacements: Array) => void | Promise<void>,
 * }} props
 */
export default function AddDrawingCameraLocationDialog({
  open,
  drawing,
  cameras,
  camerasLoading = false,
  onClose,
  onSave,
}) {
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [localPlacements, setLocalPlacements] = useState([]);
  const [placementsLoading, setPlacementsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!open || !drawing) return;
    setSelectedCameraId("");
    setLocalPlacements(
      enrichPlacementsWithCameraLabels(getDrawingCameraPlacements(drawing), cameras),
    );

    let cancelled = false;
    (async () => {
      setPlacementsLoading(true);
      try {
        const placements = await fetchPatrolSectionCameras(drawing.id);
        if (!cancelled) {
          setLocalPlacements(
            enrichPlacementsWithCameraLabels(placements, cameras),
          );
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("Failed to load camera locations for drawing.", err);
          setLocalPlacements(
            enrichPlacementsWithCameraLabels(
              getDrawingCameraPlacements(drawing),
              cameras,
            ),
          );
        }
      } finally {
        if (!cancelled) setPlacementsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, drawing, cameras]);

  /** Re-apply camera names when the catalog loads after placements. */
  useEffect(() => {
    if (!open || !cameras.length) return;
    setLocalPlacements((prev) =>
      enrichPlacementsWithCameraLabels(prev, cameras),
    );
  }, [open, cameras]);

  const displayMarkers = useMemo(
    () => markersForDisplay(localPlacements, cameras),
    [localPlacements, cameras],
  );

  const totalLocations = useMemo(
    () => countPlacementLocations(localPlacements),
    [localPlacements],
  );

  const cameraOptions = useMemo(() => {
    if (!drawing) return cameras;
    const site = drawing.title?.trim().toLowerCase();
    const matched = cameras.filter((c) => c.site?.trim().toLowerCase() === site);
    const rest = cameras.filter((c) => c.site?.trim().toLowerCase() !== site);
    return [...matched, ...rest];
  }, [cameras, drawing]);

  const selectedCamera = useMemo(
    () => cameras.find((c) => c.id === selectedCameraId),
    [cameras, selectedCameraId],
  );

  const selectedPlacement = useMemo(() => {
    if (!selectedCameraId) return null;
    return normalizePlacements(localPlacements).find(
      (p) => p.cameraId === selectedCameraId,
    );
  }, [localPlacements, selectedCameraId]);

  const placeCameraAt = useCallback(
    ({ xPercent, yPercent }) => {
      if (!selectedCameraId) {
        toast.error("Select a camera first, then click on the drawing.");
        return;
      }
      const cam = cameras.find((c) => c.id === selectedCameraId);
      if (!cam) return;

      const newLocation = {
        xPercent,
        yPercent,
        duration: DEFAULT_LOCATION_DURATION,
      };

      const wasEdit = Boolean(selectedPlacement?.locations?.length);

      setLocalPlacements((prev) => {
        const normalized = normalizePlacements(prev);
        const idx = normalized.findIndex((p) => p.cameraId === selectedCameraId);
        if (idx >= 0) {
          const next = [...normalized];
          next[idx] = {
            ...next[idx],
            label: cam.name,
            locations: [newLocation],
          };
          return next;
        }
        return [
          ...normalized,
          {
            cameraId: selectedCameraId,
            label: cam.name,
            sortOrder: normalized.length,
            locations: [newLocation],
          },
        ];
      });
      toast.success(
        wasEdit
          ? `Updated position for ${cam.name}.`
          : `Placed ${cam.name} on drawing.`,
      );
    },
    [cameras, selectedCameraId, selectedPlacement],
  );

  const handleRemoveCamera = async (cameraId) => {
    const placement = normalizePlacements(localPlacements).find(
      (p) => p.cameraId === cameraId,
    );
    if (placement?.placementId) {
      try {
        await deletePatrolSectionCamera(placement.placementId);
      } catch (err) {
        toast.error(
          err?.message || "Could not remove camera location from server.",
        );
        return;
      }
    }
    setLocalPlacements((prev) =>
      normalizePlacements(prev).filter((p) => p.cameraId !== cameraId),
    );
    if (selectedCameraId === cameraId) {
      setSelectedCameraId("");
    }
    toast.success("Camera location removed.");
  };

  const handleSave = async () => {
    if (!drawing || isSaving) return;
    const placements = normalizePlacements(localPlacements)
      .filter((p) => p.locations.length > 0)
      .map((p, index) => ({ ...p, sortOrder: index }));
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave(drawing.id, placements);
      toast.success("Camera locations saved.");
      onClose();
    } catch (err) {
      const message =
        err?.message || "Failed to save camera locations. Please try again.";
      setSaveError(message);
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!drawing) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxWidth: 920,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#101828" }}>
            Add camera location
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#6B7280", mt: 0.25 }}>
            {drawing.title}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          p: 3,
          minHeight: 420,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <FormControl
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
              disabled={camerasLoading}
            >
              <InputLabel id="patrol-camera-select-label">Camera</InputLabel>
              <Select
                labelId="patrol-camera-select-label"
                label="Camera"
                value={selectedCameraId}
                onChange={(e) => setSelectedCameraId(e.target.value)}
                sx={{ borderRadius: "10px", bgcolor: "#F9FAFB" }}
                displayEmpty
              >
                {camerasLoading ? (
                  <MenuItem value="" disabled>
                    Loading cameras…
                  </MenuItem>
                ) : cameraOptions.length === 0 ? (
                  <MenuItem value="" disabled>
                    No cameras — add them in Camera management
                  </MenuItem>
                ) : (
                  cameraOptions.map((cam) => (
                    <MenuItem key={cam.id} value={cam.id}>
                      {cam.name}
                      {cam.site ? ` · ${cam.site}` : ""} ({cam.type})
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>

          <Typography sx={{ fontSize: 13, color: "#64748B" }}>
            {selectedCamera
              ? selectedPlacement?.locations?.length
                ? `Click on the drawing to move “${selectedCamera.name}”.`
                : `Click on the drawing to place “${selectedCamera.name}”.`
              : "Select a camera, then click on the drawing (one point per camera)."}
          </Typography>

          <Box sx={{ position: "relative" }}>
            {placementsLoading ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.72)",
                  borderRadius: "10px",
                }}
              >
                <CircularProgress size={32} />
              </Box>
            ) : null}
            <PatrolDrawingMediaSurface
              drawing={drawing}
              markers={displayMarkers}
              minHeight={360}
              interactive
              enableZoom
              placementHint={Boolean(selectedCameraId)}
              onLocationPick={placeCameraAt}
            />
          </Box>
        </Box>

        <Box
          sx={{
            width: { xs: "100%", md: 280 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#364153" }}>
            Placed cameras ({totalLocations})
          </Typography>
          {totalLocations === 0 ? (
            <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
              No cameras placed on this drawing yet.
            </Typography>
          ) : (
            <List dense disablePadding sx={{ flex: 1, overflow: "auto" }}>
              {normalizePlacements(localPlacements)
                .filter((p) => p.locations.length > 0)
                .map((p, routeIndex) => {
                  const loc = p.locations[0];
                  const isSelected = p.cameraId === selectedCameraId;
                  return (
                    <ListItem
                      key={p.cameraId}
                      button
                      selected={isSelected}
                      onClick={() => setSelectedCameraId(p.cameraId)}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label="Remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveCamera(p.cameraId);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      }
                      sx={{
                        px: 1,
                        py: 0.75,
                        borderRadius: 1,
                        bgcolor: isSelected ? "#EEF2FF" : "#F9FAFB",
                        mb: 0.5,
                        border: "1px solid",
                        borderColor: isSelected ? "#2932E5" : "#E5E7EB",
                      }}
                    >
                      <VideocamIcon
                        sx={{ fontSize: 18, color: "#22C55E", mr: 1 }}
                      />
                      <ListItemText
                        primary={`${routeIndex + 1}. ${placementDisplayLabel(p, cameras)}`}
                        secondary={`Patrol stop ${routeIndex + 1} · x: ${loc.xPercent.toFixed(4)}, y: ${loc.yPercent.toFixed(4)}`}
                        primaryTypographyProps={{
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                        secondaryTypographyProps={{ fontSize: 11 }}
                      />
                    </ListItem>
                  );
                })}
            </List>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 1,
          px: 3,
          py: 2,
          bgcolor: "#F9FAFB",
          borderTop: "1px solid #E5E7EB",
        }}
      >
        {saveError ? (
          <Typography sx={{ fontSize: 13, color: "#B91C1C", alignSelf: "stretch" }}>
            {saveError}
          </Typography>
        ) : null}
        <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={isSaving}
          sx={{
            textTransform: "none",
            bgcolor: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          sx={{
            textTransform: "none",
            bgcolor: "#2932E5",
            borderRadius: "8px",
            boxShadow: "none",
            opacity: isSaving ? 0.7 : 1,
            "&:hover": { bgcolor: "#1f28b8" },
          }}
        >
          {isSaving ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            "Save locations"
          )}
        </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
